import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Stack,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Tooltip,
  TableSortLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProgramSubscription, ProgramPayment } from '../../../types/ProgramTypes';
import { BVTConstants } from '../../../../utils/BVTConstants';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import axios from '../../../../services/ApiService';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import PayPalButton from '../../../../services/PayPalService';
import { useReports } from '../../../../hooks/useReports';
import Swal from 'sweetalert2';

function CompanySubscriptionDetails() {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<ProgramSubscription | null>(null);
  const [pendingPayments, setPendingPayments] = useState<ProgramPayment[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<ProgramPayment[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { downloadReport } = useReports();
  const location = useLocation();
  const subscriptionId = (location.state as { subscriptionId?: number })?.subscriptionId;
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscription();
  }, [subscriptionId]);

  useEffect(() => {
    if (subscription) {
      filterPayments(subscription.payments);
    }
  }, [sortOrder, subscription]);

  const fetchSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/getProgramSubscription', subscriptionId);
      if (response.data) {
        setSubscription(response.data);
      }
    } catch (error) {
      //toast.error('Failed to fetch subscriptions');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = (payments: ProgramPayment[]) => {
    const pending = payments.filter(payment => 
      payment.status === 'PENDING' || payment.status === 'OVERDUE'
    );
    const history = payments.filter(payment => 
      payment.status === 'PAID' || payment.status === 'COMPLETED'
    ).sort((a, b) => {
      const dateA = new Date(a.dueDate).getTime();
      const dateB = new Date(b.dueDate).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
    
    setPendingPayments(pending);
    setPaymentHistory(history);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'paid':
      case 'completed':
        return 'success';
      case 'suspended':
      case 'overdue':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === 'NA') {
      return 'N/A';
    }
    return new Date(dateString).toLocaleDateString();
  };

  const handleDownloadInvoice = async (invoiceId: number) => {
    setLoading(true);
    try {
      const response = await axios.get(`/downloadInvoice/${invoiceId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    
    const result = await Swal.fire({
      title: 'Cancel Subscription?',
      text: `Are you sure you want to cancel the subscription for "${subscription.programName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Cancel Subscription',
      cancelButtonText: 'No, Keep Subscription',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await axios.post('/cancelProgramSubscription', subscription.subId);
        toast.success('Subscription cancelled successfully');
        fetchSubscription();
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        toast.error('Failed to cancel subscription');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSortToggle = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  if (!subscription) {
    return <LoadingSpinner loading={true} />;
  }

  return (
    <>
      <Helmet>
        <title>BVT - Subscription Details</title>
      </Helmet>
      
      <Stack spacing={3}>
        
        <Card>
          <CardHeader 
            title="Subscription Details"
            subheader={
                <Chip
                label={subscription.status}
                color={getStatusColor(subscription.status) as any}
                size="small"
                sx={{mt: 1}}
              />
            }
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {!subscription.endDate && subscription.status !== 'CANCELLED' && (
                  <Button 
                    variant="outlined" 
                    color="error"
                    onClick={handleCancelSubscription}
                    disabled={loading}
                  >
                    Cancel Subscription
                  </Button>
                )}
                <IconButton 
                  onClick={() => navigate(-1)}
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    borderRadius: '50%',
                    padding: 1,
                    '&:hover': {
                      backgroundColor: '#e0e0e0',
                    },
                  }}
                >
                  <CloseIcon sx={{ color: 'black' }} />
                </IconButton>
              </Box>
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Program Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {subscription.programName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Company
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {subscription.companyName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {subscription.currency} {subscription.amount.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Frequency
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {subscription.frequency}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Start Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(subscription.startDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  End Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(subscription.endDate)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Card 2: Pending/Overdue Payments */}
        <Card>
          <CardHeader 
            title="Pending & Overdue Payments"
            subheader={`${pendingPayments.length} payment(s) require attention`}
          />
          <Divider />
          <CardContent>
            {pendingPayments.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                    <TableCell>From Date</TableCell>
                    <TableCell>Till Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    {user?.role === 'COMPANY' && <TableCell align="center">Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingPayments.map((payment) => (
                      <TableRow key={payment.subPaymentId}>
                        <TableCell>
                          {formatDate(payment.fromDate)}
                        </TableCell>
                        <TableCell>
                          {formatDate(payment.tillDate)}
                        </TableCell>
                        <TableCell>
                          {formatDate(payment.dueDate)}
                        </TableCell>
                        <TableCell>
                          {subscription.currency} {subscription.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.status}
                            color={getStatusColor(payment.status) as any}
                            size="small"
                          />
                        </TableCell>
                        {user?.role === 'COMPANY' && (
                          <TableCell align="center">
                            <Tooltip title="Pay Now">
                                <PayPalButton
                                cart={{ type: BVTConstants.PROGRAM_SUBSCRIPTION, refId: payment.subPaymentId, amount: subscription.amount }}
                                onSuccess={(tx) => fetchSubscription()}
                                onError={(err) => console.error("Error", err)}
                                />
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No pending or overdue payments
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Card 3: Payment History */}
        <Card>
          <CardHeader title="Payment History" />
          <Divider />
          <CardContent>
            {paymentHistory.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment Period</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={true}
                          direction={sortOrder}
                          onClick={handleSortToggle}
                        >
                          Due Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Paid Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Invoice ID</TableCell>
                      <TableCell>Invoice</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentHistory.map((payment) => (
                      <TableRow key={payment.subPaymentId}>
                        <TableCell>
                          {formatDate(payment.fromDate)} - {formatDate(payment.tillDate)}
                        </TableCell>
                        <TableCell>{formatDate(payment.dueDate)}</TableCell>
                        <TableCell>{formatDate(payment.paidDate)}</TableCell>
                        <TableCell>
                          {subscription.currency} {subscription.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.status}
                            color={getStatusColor(payment.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {payment.invoiceId}
                        </TableCell>
                        <TableCell>
                          {payment.invoiceId ? (
                            <Tooltip title="Download Invoice">
                              <IconButton
                                onClick={() => downloadReport(
                                  '/generateInvoice',
                                  payment.invoiceId,
                                  `invoice-${payment.invoiceId}.pdf`
                                )}
                                disabled={loading}
                                size="small"
                                color="primary"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No payment history available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>
      
      <LoadingSpinner loading={loading} />
    </>
  );
}

export default CompanySubscriptionDetails; 